// API Configuration and Base Service - Dynamic host detection (v3.0)
const getApiBaseUrl = () => {
  // If environment variable is set and not empty, use it
  if (import.meta.env.VITE_API_URL && import.meta.env.VITE_API_URL.trim() !== '') {
    return import.meta.env.VITE_API_URL;
  }
  
  // Dynamic host detection - works on any network/IP automatically
  const { protocol, hostname, port } = window.location;
  
  // Build the base URL with port if specified
  const portPart = port ? `:${port}` : '';
  
  // Use the same host/port as the frontend is served from
  // Nginx will proxy /api/* to the backend
  return `${protocol}//${hostname}${portPart}/api/v1`;
};

const API_BASE_URL = getApiBaseUrl();

interface ApiResponse<T> {
  data?: T;
  message?: string;
  error?: string;
}

class ApiService {
  private baseURL: string;
  private token: string | null = null;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
    this.refreshToken();
  }

  refreshToken() {
    this.token = localStorage.getItem('access_token');
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('access_token', token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('access_token');
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    // Always get the latest token before making a request
    this.refreshToken();
    
    const url = `${this.baseURL}${endpoint}`;
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(this.token && { Authorization: `Bearer ${this.token}` }),
        ...options.headers,
      },
      ...options,
    };

    console.log(`Making API request to: ${url}`, {
      method: config.method || 'GET',
      hasToken: !!this.token,
      tokenPrefix: this.token ? this.token.substring(0, 10) + '...' : 'none'
    });

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error(`API Error ${response.status}:`, errorData);
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      // Handle 204 NO_CONTENT responses (e.g., DELETE)
      if (response.status === 204 || response.headers.get('content-length') === '0') {
        console.log(`API Success for ${endpoint}: (no content)`);
        return {} as T;
      }

      const result = await response.json();
      console.log(`API Success for ${endpoint}:`, result);
      return result;
    } catch (error) {
      console.error(`API request failed: ${endpoint}`, error);
      throw error;
    }
  }

  // GET request
  async get<T>(endpoint: string, options?: { params?: Record<string, string> }): Promise<T> {
    let url = endpoint;
    if (options?.params) {
      const searchParams = new URLSearchParams(options.params);
      url += `?${searchParams.toString()}`;
    }
    return this.request<T>(url, { method: 'GET' });
  }

  // POST request
  async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // PUT request
  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // PATCH request
  async patch<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // DELETE request
  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export const apiService = new ApiService();
export default apiService; 